import React, { useMemo } from "react";
import { Outlet } from "react-router-dom";

// material-ui
import { ThemeProvider, useTheme } from "@mui/material/styles";
import {
  AppBar,
  Box,
  Container,
  CssBaseline,
  GlobalStyles,
  Toolbar,
  useMediaQuery,
} from "@mui/material";

// project imports
import Breadcrumbs from "ui-component/extended/Breadcrumbs";
import Header from "./Header";
import Customization from "../Customization";
import navigation from "menu-items";
import useConfig from "hooks/useConfig";
import { openDrawer } from "store/slices/menu";
import { useDispatch } from "store";
import { C, withMemberFonts } from "themes/aurora";
// assets
import { IconChevronRight } from "@tabler/icons";

const auroraGlobal = {
  body: { background: C.bg, color: C.text },
  "::selection": { background: "rgba(124,92,255,0.45)" },
  "*::-webkit-scrollbar": { width: 8, height: 8 },
  "*::-webkit-scrollbar-thumb": {
    background: "rgba(160,170,255,0.18)",
    borderRadius: 8,
  },
  "*::-webkit-scrollbar-track": { background: "transparent" },
};

// ==============================|| MAIN LAYOUT ||============================== //

const MainLayout = () => {
  const theme = useTheme();
  const matchDownMd = useMediaQuery(theme.breakpoints.down("lg"));
  const dispatch = useDispatch();
  const { container } = useConfig();

  React.useEffect(() => {
    dispatch(openDrawer(!matchDownMd));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchDownMd]);

  const header = useMemo(
    () => (
      <Toolbar disableGutters sx={{ minHeight: { xs: 60, sm: 68 } }}>
        <Header />
      </Toolbar>
    ),
    []
  );

  const content = (
    <>
      <Breadcrumbs
        separator={IconChevronRight}
        navigation={navigation}
        icon
        title
        rightAlign
      />
      <Outlet />
    </>
  );

  return (
    <ThemeProvider theme={withMemberFonts}>
      <Box sx={{ display: "flex", minHeight: "100vh" }}>
        <CssBaseline />
        <GlobalStyles styles={auroraGlobal} />

        {/* aurora background */}
        <Box
          aria-hidden
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 0,
            pointerEvents: "none",
            background: [
              "radial-gradient(60% 50% at 8% 0%, rgba(124,92,255,0.22) 0%, transparent 60%)",
              "radial-gradient(50% 45% at 100% 10%, rgba(34,211,238,0.14) 0%, transparent 60%)",
              "radial-gradient(60% 50% at 70% 100%, rgba(236,72,153,0.10) 0%, transparent 60%)",
              `linear-gradient(180deg, ${C.bg} 0%, ${C.bgDeep} 100%)`,
            ].join(", "),
            "&::after": {
              content: '""',
              position: "absolute",
              inset: 0,
              opacity: 0.35,
              backgroundImage:
                "radial-gradient(rgba(255,255,255,0.08) 1px, transparent 1px)",
              backgroundSize: "28px 28px",
              maskImage:
                "linear-gradient(180deg, rgba(0,0,0,0.9) 0%, transparent 70%)",
              WebkitMaskImage:
                "linear-gradient(180deg, rgba(0,0,0,0.9) 0%, transparent 70%)",
            },
          }}
        />

        {/* header */}
        <AppBar
          enableColorOnDark
          position="fixed"
          color="inherit"
          elevation={0}
          sx={{
            background: "rgba(7, 10, 20, 0.72)",
            backdropFilter: "blur(18px) saturate(160%)",
            WebkitBackdropFilter: "blur(18px) saturate(160%)",
            borderBottom: `1px solid ${C.border}`,
            boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
          }}
        >
          {header}
        </AppBar>

        {/* main content */}
        <Box
          component="main"
          sx={{
            position: "relative",
            zIndex: 1,
            flexGrow: 1,
            width: "100%",
            minHeight: "100vh",
            pt: { xs: "76px", sm: "92px" },
            color: C.text,
          }}
        >
          {container ? <Container maxWidth="lg">{content}</Container> : content}
        </Box>
        <Customization />
      </Box>
    </ThemeProvider>
  );
};

export default MainLayout;
